import { formatDistanceStrict } from 'date-fns';
import clsx from 'clsx';
import type { Booking, Deposit, CustomerRecord, SessionLength } from '@/domain/types';
import { aud, customerName } from '@/utils/format';

const SIZE_LABEL: Record<SessionLength, string> = {
  small: 'Small',
  'half-day': 'Half day',
  'full-day': 'Full day',
  'multi-session': 'Multi',
};

/** Time-to-lapse for a hold's linked deposit, read against the sim clock. */
function lapseLabel(deposit: Deposit | undefined, clockIso: string): string {
  if (!deposit) return 'no link';
  const expires = new Date(deposit.expiresAt).getTime();
  const now = new Date(clockIso).getTime();
  if (expires <= now) return 'lapsed';
  return `${formatDistanceStrict(new Date(deposit.expiresAt), new Date(clockIso))} left`;
}

interface StackedHoldsTileProps {
  /** Stable cluster id (earliest-starting hold) — drives the testid. */
  id: string;
  /** The competing overbookable holds (any order; sorted here by lapse time). */
  holds: Booking[];
  deposits: Record<string, Deposit>;
  customers: Record<string, CustomerRecord>;
  clockIso: string;
  onOpen: (bookingId: string) => void;
  /** Absolute positioning from the calendar column (top/height/left/width). */
  style: React.CSSProperties;
}

/**
 * The stacked competing holds cell (feat/ui-holds). When a this-week slot carries
 * multiple overbookable ghost holds, the calendar shows them as ONE tile at
 * normal booking width with the holder list visible by default, sorted
 * soonest-to-lapse. It never splits the column into skinny lanes: the holders
 * grow DOWN the slot's height. When one pays, the model voids the others and the
 * tile naturally collapses to a single locked booking.
 *
 * Ghost styling: a dashed, blood-tinted treatment, matching the dashed
 * superseded/held precedent, so the competing holds read as tentative.
 */
export default function StackedHoldsTile({
  id,
  holds,
  deposits,
  customers,
  clockIso,
  onOpen,
  style,
}: StackedHoldsTileProps) {
  // Soonest-to-lapse first, keyed off the linked deposit's expiry. A hold with no
  // linked deposit sorts last (nothing to lapse against yet).
  const sorted = holds.slice().sort((a, b) => {
    const ea = deposits[a.depositId ?? '']?.expiresAt;
    const eb = deposits[b.depositId ?? '']?.expiresAt;
    if (!ea && !eb) return 0;
    if (!ea) return 1;
    if (!eb) return -1;
    return new Date(ea).getTime() - new Date(eb).getTime();
  });

  return (
    <div
      data-testid={`stacked-holds-${id}`}
      style={style}
      className="absolute rounded-md border border-dashed border-blood/40 bg-blood/5 overflow-hidden shadow-ink-soft flex flex-col"
    >
      <div className="flex items-center justify-between gap-1 px-2 py-1 border-b border-dashed border-blood/30 bg-blood/10 shrink-0">
        <span className="label-small-caps text-blood truncate">Overbookable</span>
        <span className="font-mono text-[10px] tabular-nums text-blood shrink-0">
          {sorted.length} holding
        </span>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-dashed divide-blood/20">
        {sorted.map((hold, i) => {
          const deposit = deposits[hold.depositId ?? ''];
          const cust = customers[hold.customerId];
          const lead = i === 0;
          return (
            <button
              key={hold.id}
              type="button"
              onClick={() => onOpen(hold.id)}
              title={`${cust ? customerName(cust) : 'Hold'} · ${lapseLabel(deposit, clockIso)}`}
              data-testid={`stacked-hold-row-${hold.id}`}
              className={clsx(
                'w-full text-left px-2 py-1 transition hover:bg-blood/10',
                lead ? 'text-cream' : 'text-cream-soft',
              )}
            >
              <span className="flex items-center justify-between gap-1">
                <span className="font-inter text-[12px] leading-tight truncate">
                  {cust ? customerName(cust) : 'Hold'}
                </span>
                <span
                  className={clsx(
                    'font-mono text-[9px] tabular-nums shrink-0',
                    lead ? 'text-blood' : 'text-cream-faint',
                  )}
                >
                  {lapseLabel(deposit, clockIso)}
                </span>
              </span>
              <span className="flex items-center justify-between gap-1 mt-0.5">
                <span className="font-inter text-[9px] uppercase tracking-widest text-cream-faint truncate">
                  {SIZE_LABEL[hold.sessionLength]}
                </span>
                {deposit ? (
                  <span className="font-mono text-[9px] tabular-nums text-cream-soft shrink-0">
                    {aud(deposit.amountAud)}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
